/**
 * Auto Alt Text — fills in missing image alt attributes
 * based on the image filename.
 *
 * ONLY touches images where alt is missing or empty.
 * Existing alt text is never overwritten.
 *
 * Handles lazy-loaded images (lazysizes, lozad, native theme
 * lazyload patterns using data-src / data-srcset / data-original)
 * by watching for attribute changes, not just new elements.
 *
 * Add this file to your theme (e.g. assets/auto-alt-text.js)
 * and include it in theme.liquid, right before </body>:
 *   <script src="{{ 'auto-alt-text.js' | asset_url }}" defer></script>
 */
(function () {
  "use strict";

  // Attributes (in priority order) that might hold the real image URL
  // before a lazy-load library swaps it into `src`.
  var SRC_ATTRS = [
    "data-src",
    "data-original",
    "data-lazy-src",
    "data-srcset",
    "data-src-srcset",
    "srcset",
    "src",
  ];

  function firstUrlFromSrcset(value) {
    if (!value) return "";
    // srcset format: "url1 100w, url2 200w, ..." — take the first URL
    var first = value.split(",")[0].trim();
    return first.split(/\s+/)[0] || "";
  }

  function getImageSource(img) {
    for (var i = 0; i < SRC_ATTRS.length; i++) {
      var attr = SRC_ATTRS[i];
      var val = attr === "src" ? img.currentSrc || img.src : img.getAttribute(attr);
      if (!val) continue;
      if (attr.indexOf("srcset") !== -1) {
        val = firstUrlFromSrcset(val);
      }
      if (val && val.indexOf("data:image") !== 0) {
        return val;
      }
    }
    return "";
  }

  function filenameToAltText(src) {
    try {
      var url = new URL(src, window.location.href);
      var filename = url.pathname.split("/").pop();

      // Strip extension
      filename = filename.replace(/\.[a-zA-Z0-9]+$/, "");

      // Strip Shopify-style size/crop suffixes, e.g. _1234x1234_crop_center
      filename = filename.replace(/_\d+x\d*(_crop_[a-z]+)?$/i, "");
      filename = filename.replace(/_crop_[a-z]+$/i, "");

      // Strip trailing hash/id-looking chunks
      filename = filename.replace(/[-_][a-f0-9]{6,}$/i, "");
      filename = filename.replace(
        /[-_][0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i,
        ""
      );

      // Strip a trailing purely-numeric id
      filename = filename.replace(/[-_]\d{4,}$/, "");

      // Replace dashes/underscores with spaces
      filename = filename.replace(/[-_]+/g, " ").trim();

      if (!filename) return "";

      return filename
        .split(" ")
        .filter(Boolean)
        .map(function (word) {
          return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
        })
        .join(" ");
    } catch (e) {
      return "";
    }
  }

  function needsAlt(img) {
    var currentAlt = img.getAttribute("alt");
    return currentAlt === null || currentAlt.trim() === "";
  }

  function applyAlt(img) {
    if (!needsAlt(img)) return;
    var src = getImageSource(img);
    if (!src) return;
    var generatedAlt = filenameToAltText(src);
    if (generatedAlt) {
      img.setAttribute("alt", generatedAlt);
    }
  }

  function fillMissingAltText(root) {
    var scope = root || document;
    if (scope.tagName === "IMG") {
      applyAlt(scope);
      return;
    }
    var images = scope.querySelectorAll("img");
    Array.prototype.forEach.call(images, applyAlt);
  }

  function runFullPass() {
    fillMissingAltText(document);
  }

  // Initial passes: as soon as possible, then again after full load
  // (covers images whose lazy-loader only swaps src after window "load").
  document.addEventListener("DOMContentLoaded", runFullPass);
  window.addEventListener("load", runFullPass);

  // Catch late-rendering sections (cart drawers, quick view, apps that
  // inject markup a second or two after load).
  [500, 1500, 3000].forEach(function (delay) {
    setTimeout(runFullPass, delay);
  });

  // Watch for:
  //  1) new <img> elements being added to the DOM
  //  2) existing <img> elements having their src/data-src/srcset swapped
  //     in by a lazy-load library once they scroll into view
  var observer = new MutationObserver(function (mutations) {
    mutations.forEach(function (mutation) {
      if (mutation.type === "attributes") {
        if (mutation.target.tagName === "IMG") {
          applyAlt(mutation.target);
        }
        return;
      }
      mutation.addedNodes.forEach(function (node) {
        if (node.nodeType !== 1) return; // only elements
        if (node.tagName === "IMG") {
          applyAlt(node);
        } else if (node.querySelectorAll) {
          fillMissingAltText(node);
        }
      });
    });
  });

  observer.observe(document.documentElement, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: [
      "src",
      "data-src",
      "data-original",
      "data-lazy-src",
      "srcset",
      "data-srcset",
    ],
  });
})();